// Magnetic centre snapping for an element that is being dragged.
//
// The centre of the element is pulled onto the centre of the print area it is
// being dragged in - horizontally, vertically or both - as soon as it comes
// within SNAP_THRESHOLD of it, and a dashed guide line is shown on the axis
// that caught. The pull is measured against the position the drag asked for,
// never against the snapped position, so walking away from the centre releases
// the element the moment the pointer passes the threshold: no hysteresis, and
// no need for the pointer-delta guard the old implementation used.
//
// Boxes that touch or overlap are one print area to the customer, so the centre
// snapped to is the centre of the whole region rather than of whichever box the
// element happens to be over.
//
// No imports on purpose - this is plain math on centres and bounds in canvas
// coordinates, and is unit tested directly.

// How close the centre has to get, in canvas pixels, before it is caught.
export const SNAP_THRESHOLD = 10;

function boundsCenter(bounds) {
	return { x: (bounds.x0 + bounds.x1) / 2, y: (bounds.y0 + bounds.y1) / 2 };
}

// The print area a dragged element snaps to. The region the element's centre
// sits in, so the target does not change while the element is being moved
// around inside one area. Once the centre is outside every area - which a drag
// is free to do, it is only flagged, not refused - the nearest region's centre
// wins, so the element is still caught on the way back in.
export function snapBoundsFor(groups, center) {
	if (!groups || groups.length === 0 || !center) return null;

	let nearest = null;
	let nearestDistance = Infinity;

	for (const group of groups) {
		const bounds = group && group.bounds;
		if (!bounds) continue;

		// Inside this region: done, whatever the other regions are doing.
		if (center.x >= bounds.x0 && center.x <= bounds.x1 &&
			center.y >= bounds.y0 && center.y <= bounds.y1) return bounds;

		const c = boundsCenter(bounds);
		const distance = (c.x - center.x) ** 2 + (c.y - center.y) ** 2;
		if (distance < nearestDistance) {
			nearestDistance = distance;
			nearest = bounds;
		}
	}
	return nearest;
}

// Where the centre of the element goes. A null axis means that axis did not
// catch and keeps the position the drag gave it; a number is the coordinate to
// put the centre on, and is also where the guide line for that axis is drawn.
export function centerSnap(center, bounds, threshold = SNAP_THRESHOLD) {
	if (!center || !bounds) return { x: null, y: null };
	const c = boundsCenter(bounds);
	return {
		x: Math.abs(center.x - c.x) <= threshold ? c.x : null,
		y: Math.abs(center.y - c.y) <= threshold ? c.y : null
	};
}
